import { AiFeedbackForm, Button, DataTable, Drawer, Group, Icon, Tag, Title } from "components/common"
import { forwardRef, useImperativeHandle, useMemo, useRef, useState } from "react"

import { Tabs } from "antd"
import { Assessment } from "features/clients/components/callsTab/assesment"
import CallDetailCard from "features/clients/components/callsTab/callDetailCard"
import CallSummary from "features/clients/components/callsTab/callSummary"
import { ClientInformation } from "features/clients/components/callsTab/clientInformation"
import MktgCallSummary from "features/clients/components/callsTab/mktgCallSummary"
import Transcription from "features/clients/components/callsTab/transcription"
import { useGetCallDetailQuery } from "features/clients/detail/api"
import { t } from "i18next"
import { IoMdArrowBack } from "react-icons/io"
import { convertToReadableString } from "utils"
import { MeetingAINotes } from "../../../management/components/meetingNotesTab/meetingAINotes"

const ClientTabsSkeleton = () => {
  return <DataTable loading data={[]} columns={[]} />
}

const CallDrawer = forwardRef(
  ({ selectedCall, onBack, canAddFeedback, from, useQuery = useGetCallDetailQuery }, ref) => {
    const isMeeting = selectedCall?.audioAnalysis

    const [showCallsDrawer, setShowCallsDrawer] = useState(false)
    const [tabKey, setTabKey] = useState("summary")
    const aiFeedbackRef = useRef()

    const toggleDrawer = () => {
      setShowCallsDrawer(prev => !prev)
    }

    useImperativeHandle(ref, () => ({
      toggleDrawer,
    }))

    const taskType = selectedCall?.phoneCallId ? "phone_call" : isMeeting ? "management_meeting" : "task"

    const { data: callDetail, isFetching: fetchingCallDetail } = useQuery(
      {
        taskId: taskType === "task" || taskType === "management_meeting" ? selectedCall?.id : selectedCall?.phoneCallId,
        taskType: taskType,
      },
      {
        skip: !selectedCall,
      }
    )

    const combinedCallDetail = { ...(selectedCall ?? {}), ...(callDetail ?? {}) }

    const callTabs = () => [
      {
        label: t("Summary"),
        key: "summary",
        children: isMeeting ? (
          <MeetingAINotes details={combinedCallDetail?.summary} />
        ) : from === "mktg" ? (
          <MktgCallSummary callDetail={combinedCallDetail} />
        ) : (
          <CallSummary callDetail={combinedCallDetail} />
        ),
      },
      ...(callDetail?.transcription?.length
        ? [
            {
              label: t("Transcription"),
              key: "transcription",
              children: <Transcription transcription={callDetail?.transcription} />,
            },
          ]
        : []),
      ...(callDetail?.clientInformation
        ? [
            {
              label: t("Client Information"),
              key: "clientInfo",
              children: <ClientInformation data={callDetail?.clientInformation} />,
            },
          ]
        : []),
      ...(callDetail?.assessment || selectedCall?.audioAnalysis?.summary?.assessment
        ? [
            {
              label: t("Assessment"),
              key: "assessment",
              children: (
                <Assessment
                  data={callDetail?.assessment || selectedCall?.audioAnalysis?.summary?.assessment}
                  isMeeting={isMeeting}
                />
              ),
            },
          ]
        : []),
    ]

    const getTagColor = tag => {
      switch (tag) {
        case "Fake":
          return "#e83a3a"
        case "Meeting Approved":
          return "#00A4D1"
        case "Meeting Rejected":
          return "#e83a3a"
        case "Qualified Lead":
          return "#00A4D1"
        case "Unqualified Lead":
          return "#e83a3a"
        default:
          return "#00A4D1"
      }
    }

    const renderCallTags = useMemo(() => {
      return from === "mktg" ? (
        <Tag className="mbe-8" color={getTagColor(callDetail?.summary?.status)}>
          {callDetail?.summary?.status}
        </Tag>
      ) : selectedCall?.phoneCallId ? (
        callDetail?.callTags?.map((tag, index) => (
          <Tag className="mbe-8" key={index} color={getTagColor(tag)}>
            {tag}
          </Tag>
        ))
      ) : (
        <Tag className="mbe-8" color={getTagColor()}>
          {convertToReadableString(callDetail?.summary?.meeting?.type_of_meeting)}
        </Tag>
      )
    }, [callDetail])

    return (
      <>
        <Drawer
          title={
            <Group template="auto 1fr" className="flexYcenter">
              <IoMdArrowBack
                onClick={() => {
                  onBack(toggleDrawer)
                }}
              />
              <div>
                <Title level={3} className="mbe-0">
                  {isMeeting ? t("Meeting Analysis") : t("Voice Transcription")}
                </Title>
                <div className="gray-700 fs12 lhn">
                  {isMeeting
                    ? t("Meeting summarised by AI, transcription and assessment metrics")
                    : t("Revolutionising the way we transcribe conversations with clients")}
                </div>
              </div>
            </Group>
          }
          headerStyle={{ paddingBlock: 20 }}
          closable={false}
          placement="right"
          width={isMeeting ? 900 : 700}
          onClose={toggleDrawer}
          open={showCallsDrawer}
          // className={styles.filterMain}
          extra={<Icon icon="IoMdClose" onClick={toggleDrawer} />}
          destroyOnClose={true}
        >
          {fetchingCallDetail ? (
            <ClientTabsSkeleton />
          ) : (
            <div>
              {!isMeeting && renderCallTags}
              <CallDetailCard callDetail={combinedCallDetail} />
              <Tabs
                activeKey={tabKey}
                {...(canAddFeedback && {
                  tabBarExtraContent: {
                    right: (
                      <Button
                        icon="MdOutlineRateReview"
                        type="secondary-light"
                        onClick={() => {
                          aiFeedbackRef.current?.setVisible(true)
                        }}
                        text={t("Feedback")}
                      />
                    ),
                  },
                })}
                items={callTabs()}
                onChange={key => {
                  setTabKey(key)
                }}
              />
            </div>
          )}
        </Drawer>
        <AiFeedbackForm
          ref={aiFeedbackRef}
          entityId={
            taskType === "task" || taskType === "management_meeting" ? selectedCall?.id : selectedCall?.phoneCallId
          }
          entity={isMeeting ? "management_meeting" : "call_transcription"}
          from={"meetingAIDrawer"}
        />
      </>
    )
  }
)
export default CallDrawer
